import React, { useState } from 'react';
import { Grid, Button, FormControl, Tooltip, Chip, Stack, Alert, DialogActions, DialogContent } from '@mui/material';
import { DialogCustom, DialogTitleCustom, FieldWrap, FetchAutocomplete } from '@tap/mui-ui';
import { LoadingButton } from '@mui/lab';
import { ICancelDialogResult, IDialogProps } from '@tap/tap-ui';
import { userService, IApiUserAccount } from '@tap/data-providers';
import { CommentEditor, useComment } from '@tap/doc-ui';

/*
*   Диалог: Назначение исполнителя
*/


type IAssignUserDialogProps = IDialogProps<{ issueKeys: string[] }, ICancelDialogResult>;

export default function AssignUserDialog({
    data,
    isOpen,
    handleClose,
}: IAssignUserDialogProps) {
    const [itemKeys, setItemKeys] = useState(data.issueKeys);
    const [topErrorMessage, setTopErrorMessage] = useState('');
    const [errorDict, setErrorDict] = useState<any>({});
    const [unhandledErrorMessage, setUnhandledErrorMessage] = useState('');

    const refreshDialogState = React.useCallback(async () => {
        setItemKeys(data.issueKeys);
        setErrorDict({});
        setTopErrorMessage('');
        setUnhandledErrorMessage('');
    }, [data.issueKeys]);

    React.useEffect(() => {
        refreshDialogState();
    }, [data.issueKeys, refreshDialogState]);

    const [selectedUser, setSelectedUser] = React.useState<IApiUserAccount | null>(null);
    const commentState = useComment({ documentKeys: itemKeys, actionCode: 'issue__assign' });
    const [isLoading, setIsLoading] = useState(false);


    const handleRemoveItem = (removedKey: string) => {
        setItemKeys(itemKeys.filter(id => id !== removedKey));
    }

    const handleCancel = () => {
        handleClose({ isCancel: true });
    }

    const handleAssign = () => {
        setIsLoading(true);
        return Promise.resolve()
            .then(() => {
                commentState.addComment();
            })
            .then(() => {
                setIsLoading(false);
                handleClose({ isCancel: false });
            });
    }


    return (
        <DialogCustom
            handleClose={handleCancel}
            open={isOpen}
        >
            <DialogTitleCustom
                primaryText={itemKeys.length === 1 ? `Назначение исполнителя для ${itemKeys[0]}` : 'Назначение исполнителя'}
            />
            <DialogContent>

                {Boolean(topErrorMessage) && (
                    <Alert severity="error" sx={{ marginBottom: '15px' }}>{topErrorMessage}</Alert>
                )}

                <Grid
                    item
                    container
                    spacing={2}
                    alignItems='stretch'
                >
                    {itemKeys.length > 1 && (
                        <FieldWrap label={'Задания'}>
                            <Stack direction="row"
                                spacing={1}
                                sx={{ marginBottom: '15px' }}
                            >
                                {itemKeys.map(itemKey => (
                                    <Tooltip
                                        key={itemKey}
                                        title={errorDict[itemKey]}
                                    >
                                        <Chip
                                            label={itemKey}
                                            variant="outlined"
                                            color={errorDict[itemKey] ? 'error' : 'default'}
                                            onDelete={() => handleRemoveItem(itemKey)}
                                        />
                                    </Tooltip>

                                ))}
                            </Stack>
                        </FieldWrap>
                    )}

                    {/* Пользователь */}
                    <FieldWrap label={'Пользователь'}>

                        <FetchAutocomplete
                            fetchRequest={({ searchText }) => {
                                return userService.getWorkerList({ searchText }).then((res) => res);
                            }}
                            value={selectedUser}
                            onChange={(event: any, newValue: IApiUserAccount | null) => {
                                setSelectedUser(newValue);
                            }}
                            textFieldProps={{
                                name: 'selectedUser',
                            }}
                            getOptionLabel={(option) => userService.getUserFIO(option)}
                            noOptionsText={'Пользователь не найден'}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                    </FieldWrap>

                    {/* Комментарий */}
                    <FieldWrap label={'Комментарий'}>
                        <FormControl sx={{ width: '100%' }}>
                            <CommentEditor {...commentState} />
                        </FormControl>
                    </FieldWrap>
                </Grid>
            </DialogContent>
            <DialogActions >
                <Grid container justifyContent='flex-start'>
                    {unhandledErrorMessage && (<Alert severity="error">{unhandledErrorMessage}</Alert>)}
                </Grid>

                <Grid container justifyContent='flex-end'>

                    {/* Кнопка - Назначить*/}
                    <LoadingButton
                        loading={isLoading}
                        disableElevation
                        type='button'
                        variant='contained'
                        color='primary'
                        onClick={handleAssign}
                        sx={{ mr: 2 }}
                        disabled={!selectedUser}
                    >
                        Назначить
                    </LoadingButton>

                    {/* Кнопка - Отмена*/}
                    <Button
                        disableElevation
                        type='button'
                        variant='text'
                        color='primary'
                        onClick={handleCancel}
                    >
                        Отмена
                    </Button>
                </Grid>
            </DialogActions>
        </DialogCustom >
    );
}
